import React, { useState, useEffect, useContext, useRef, useMemo } from 'react'
import { toast } from 'react-toastify';
import Calendar from "@sbmdkl/nepali-datepicker-reactjs";
import "@sbmdkl/nepali-datepicker-reactjs/dist/index.css";
import DataTable from 'react-data-table-component';
import { Fetchdata } from '../../hooks/getData';
import { GetCurrMonth, GetCurrYear } from '../../hooks/dateConvertor';
import SidebarContext from '../../sidebarcontext/SidebarContext';
import Tippy from '@tippyjs/react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const DeviceCodeReport = () => {
    const userinfo = JSON.parse(localStorage.getItem("userInfo"));
    const { appURL, customStyles, currentYear } = useContext(SidebarContext);

    const [config, setConfig] = useState({
        reportCategory: "dateWise",
        class: "",
        section: "",
        value: "",
    });

    const [classList, setClassList] = useState([]);
    const [sectionList, setSectionList] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [selectYear, setSelectYear] = useState("");
    const [selectMonth, setSelectMonth] = useState("");
    const [searchText, setSearchText] = useState("");
    const [selectView, setSelectView] = useState("1");
    const [selectedSortBy, setSelectedSortBy] = useState("A");
    const searchInput = useRef(null);

    let date = [];
    for (let i = 2075; i <= currentYear; i++) {
        date.push(i);
    }

    // Separate handlers for different fields
    const handleReportCategoryChange = (value) => {
        const currYear = GetCurrYear();
        const currMonth = GetCurrMonth();
        setSelectYear(value === "monthWise" ? currYear : "");
        setSelectMonth(value === "monthWise" ? currMonth : "");
        setConfig({
            reportCategory: value,
            value: value === "monthWise" ? currYear + "/" + currMonth : "",
            class: "",
            section: "",
        });
        setSelectView("1");
        setReportData([]);
        setFilteredData([]);
        setSearchText("");
        if (searchInput.current) {
            searchInput.current.value = "";
        }
    }

    const handleView = (e) => {
        setSelectView(e.target.value);
    };

    const handleClassChange = (value) => {
        setConfig({
            ...config,
            class: value,
            section: "",
        });
        setReportData([]);
        setFilteredData([]);
        setSearchText("");
        if (searchInput.current) {
            searchInput.current.value = "";
        }
    }

    const handleSectionChange = (value) => {
        setConfig({
            ...config,
            section: value,
        });
        setReportData([]);
        setFilteredData([]);
        setSearchText("");
        if (searchInput.current) {
            searchInput.current.value = "";
        }
    }

    const handleFromDate = ({ bsDate }) => {
        setConfig({ ...config, value: bsDate });
        setReportData([]);
        setFilteredData([]);
    };

    const handleYear = (e) => {
        const year = e.target.value;
        setSelectYear(year);
        setReportData([]);
        setFilteredData([]);
        if (year && selectMonth) {
            setConfig({ ...config, value: year + "/" + selectMonth });
        } else {
            setConfig({ ...config, value: "" });
        }
    };

    const handleMonthChange = (e) => {
        const month = e.target.value;
        setSelectMonth(month);
        setReportData([]);
        setFilteredData([]);
        if (selectYear && month) {
            setConfig({ ...config, value: selectYear + "/" + month });
        } else {
            setConfig({ ...config, value: "" });
        }
    };

    // Search handler
    const searchHandler = (e) => {
        const searchTerm = e.target.value.toLowerCase();
        setSearchText(searchTerm);

        if (searchTerm === "") {
            setFilteredData(reportData);
        } else {
            const filtered = reportData.filter(item => {
                const studentName = (item.StaffName || item.FullName || item.StudentName || "").toLowerCase();
                const regNo = (item.RegNo || "").toLowerCase();
                const date = (item.AttenDate || "").toLowerCase();
                const day = (item.AttenDay || item.Day || "").toLowerCase();
                const month = (item.Month || "").toLowerCase();
                const checkIn = (item.CheckIn || "").toLowerCase();
                const checkOut = (item.CheckOut || "").toLowerCase();

                return studentName.includes(searchTerm) ||
                    regNo.includes(searchTerm) ||
                    date.includes(searchTerm) ||
                    day.includes(searchTerm) ||
                    month.includes(searchTerm) ||
                    checkIn.includes(searchTerm) ||
                    checkOut.includes(searchTerm);
            });
            setFilteredData(filtered);
        }
    };

    // API to get class list
    useEffect(() => {
        getClassList();
    }, []);

    const getClassList = () => {
        const params = {
            SchID: userinfo?.SchID,
            UserID: userinfo?.UserType === "T" ? userinfo?.UserID : "-1",
            DFlag: "C",
            Flag: "S",
            Type: "POST",
            Academicyear: userinfo?.AcademicYear,
            FetchURL: `${appURL}api/admin/ref-value`,
        };

        Fetchdata(params).then(function (result) {
            if (result.StatusCode === 200) {
                const postResult = result.referencelst ? result.referencelst : "";
                setClassList(postResult);
            } else {
                setClassList([]);
            }
        }).catch(error => {
            console.error("Error fetching class list:", error);
            setClassList([]);
            toast.error("Failed to fetch class list");
        });
    };

    // API to get section list
    useEffect(() => {
        if (config.class) {
            getSectionList();
        } else {
            setSectionList([]);
        }
    }, [config.class]);

    const getSectionList = () => {
        const params = {
            SchID: userinfo?.SchID,
            UserID: userinfo?.UserType === "T" ? userinfo?.UserID : "-1",
            ClassID: config.class,
            DFlag: "S",
            Flag: "S",
            Type: "POST",
            Academicyear: userinfo?.AcademicYear,
            FetchURL: `${appURL}api/admin/ref-value`,
        };

        Fetchdata(params).then(function (result) {
            if (result.StatusCode === 200) {
                const postResult = result.referencelst ? result.referencelst : "";
                setSectionList(postResult);
            } else {
                setSectionList([]);
            }
        }).catch(error => {
            console.error("Error fetching section list:", error);
            setSectionList([]);
            toast.error("Failed to fetch section list");
        });
    };

    // API to get Date Wise Report
    const getDateWiseReport = () => {
        setIsLoading(true);
        setIsGenerating(true);
        const params = {
            SchID: userinfo?.SchID,
            UserID: userinfo?.UserType === "T" ? userinfo?.UserID : "-1",
            Flag: "D",
            DFlag: "N",
            ClassID: config.class || "",
            SectionID: config.section || "",
            Value: config.value || "",
            Type: "POST",
            FetchURL: `${appURL}api/admin/student-atten-report`,
        };

        Fetchdata(params).then(function (result) {
            setIsLoading(false);
            setIsGenerating(false);
            if (result.StatusCode === 200) {
                const postResult = result.AttenLst ? result.AttenLst : [];
                setReportData(postResult);
                setFilteredData(postResult);
            } else {
                setReportData([]);
                setFilteredData([]);
                toast.error(result.Message || "Failed to fetch date wise report");
            }
        }).catch(error => {
            setIsLoading(false);
            setIsGenerating(false);
            console.error("Error fetching date wise report:", error);
            toast.error("Failed to fetch date wise report");
            setReportData([]);
            setFilteredData([]);
        });
    };

    // API to get Month Wise Report
    const getMonthWiseReport = () => {
        setIsLoading(true);
        setIsGenerating(true);
        const params = {
            SchID: userinfo?.SchID,
            UserID: userinfo?.UserType === "T" ? userinfo?.UserID : "-1",
            Flag: "M",
            DFlag: "N",
            ClassID: config.class || "",
            SectionID: config.section || "",
            Value: config.value || "",
            Type: "POST",
            FetchURL: `${appURL}api/admin/student-atten-report`,
        };
        // console.log('params', params);
        Fetchdata(params).then(function (result) {
            setIsLoading(false);
            setIsGenerating(false);
            if (result.StatusCode === 200) {
                const postResult = result.AttenLst ? result.AttenLst : [];
                setReportData(postResult);
                setFilteredData(postResult);
            } else {
                setReportData([]);
                setFilteredData([]);
                toast.error(result.Message || "Failed to fetch month wise report");
            }
        }).catch(error => {
            setIsLoading(false);
            setIsGenerating(false);
            console.error("Error fetching month wise report:", error);
            toast.error("Failed to fetch month wise report");
            setReportData([]);
            setFilteredData([]);
        });
    };

    // Fetch report when all required fields are selected
    useEffect(() => {
        if (config.reportCategory && config.class && config.section && config.value) {
            switch (config.reportCategory) {
                case "dateWise":
                    getDateWiseReport();
                    break;
                case "monthWise":
                    getMonthWiseReport();
                    break;
                default:
                    break;
            }
        }
    }, [config.class, config.section, config.value, config.reportCategory]);

    // Export to Excel
    const handleExportExcel = () => {
        if (!reportData.length) {
            toast.warning("No data to export");
            return;
        }

        const className = classList.find(c => c.ClassID === config.class)?.ClassName || "";
        const sectionName = sectionList.find(s => s.SectionID === config.section)?.SectionName || "";

        // Matrix export for monthWise View 1 and View 2
        if (config.reportCategory === "monthWise" && (selectView === "1" || selectView === "2")) {
            const exportData = processedData.newList.map((student, i) => {
                const row = {};
                row["S.N."] = i + 1;
                row["Student Name"] = student.StudentName;
                if (hasRegNo) {
                    row["Reg. No."] = student.data[0]?.RegNo || "";
                }
                processedData.uniqueDates.forEach(date => {
                    const data = processedData.attendanceData[student.StudentName][date];
                    if (data) {
                        row[`${date} (Check In)`] = data.CheckIn || "No Check In";
                        row[`${date} (Check Out)`] = data.CheckOut || "No Check Out";
                    } else {
                        row[`${date} (Check In)`] = "No Check In";
                        row[`${date} (Check Out)`] = "No Check Out";
                    }
                });
                return row;
            });

            const ws = XLSX.utils.json_to_sheet(exportData);
            ws['!cols'] = Object.keys(exportData[0] || {}).map(() => ({ wch: 15 }));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, `Attendance_Report`);
            XLSX.writeFile(wb, `Attendance_${className}_${sectionName}_${config.value}.xlsx`);
            toast.success("Excel file exported successfully");
            return;
        }

        const exportData = filteredData.map((item, index) => {
            const baseData = {
                "S.N.": index + 1,
                "Student Name": item.StaffName || item.FullName || item.StudentName || "",
                "Reg. No.": item.RegNo || "",
            };

            switch (config.reportCategory) {
                case "dateWise":
                    return {
                        ...baseData,
                        "Date": item.AttenDate || "",
                        "Day": item.AttenDay || "",
                        "Check In": item.CheckIn || "-",
                        "Check Out": item.CheckOut || "-",
                        "Check In Via": item.CheckInVia || "-",
                        "Check Out Via": item.CheckOutVia || "-",
                        "Check In Place": item.CheckInPlace || "-",
                        "Check Out Place": item.CheckOutPlace || "-",
                        "Status": item.CheckIn && item.CheckOut ? "Present" : (item.CheckIn ? "Partial" : "Absent"),
                        "Is Verified": item.IsVerified === "Y" ? "Yes" : "No",
                        "Verified By": item.VerifiedBy || "-",
                    };
                case "monthWise":
                    return {
                        ...baseData,
                        "Month": item.Month || "",
                        "Present Days": item.PresentDays || 0,
                        "Absent Days": item.AbsentDays || 0,
                        "Total Days": item.TotalDays || (item.PresentDays + item.AbsentDays),
                        "Attendance %": item.AttendancePercentage ? `${item.AttendancePercentage}%` : "0%",
                    };
                default:
                    return baseData;
            }
        });

        const ws = XLSX.utils.json_to_sheet(exportData);

        ws['!cols'] = Object.keys(exportData[0] || {}).map(() => ({ wch: 15 }));

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, `Attendance_Report`);
        XLSX.writeFile(wb, `Attendance_${className}_${sectionName}_${config.value}.xlsx`);
        toast.success("Excel file exported successfully");
    };

    // Export to PDF
    const handleExportPDF = () => {
        if (!reportData.length) {
            toast.warning("No data to export");
            return;
        }

        const className = classList.find(c => c.ClassID === config.class)?.ClassName || "";
        const sectionName = sectionList.find(s => s.SectionID === config.section)?.SectionName || "";

        const doc = new jsPDF("l", "pt", "a4", true);

        const midPage = doc.internal.pageSize.getWidth() / 2;

        doc.setFontSize(18);
        doc.text(`${className} - ${sectionName}`, midPage, 40, null, null, "center");

        doc.setFontSize(12);
        const reportLabel = config.reportCategory === "dateWise" ? "Date Wise" : "Month Wise";
        doc.text(`Attendance Report (${reportLabel})`, midPage, 58, null, null, "center");
        doc.setFontSize(10);
        doc.text(`Date: ${config.value}`, midPage, 73, null, null, "center");

        // Matrix export for monthWise View 1 and View 2
        if (config.reportCategory === "monthWise" && (selectView === "1" || selectView === "2")) {
            const columns = ["S.N.", "Student Name"];
            if (hasRegNo) columns.push("Reg. No.");
            processedData.uniqueDates.forEach(date => {
                columns.push(`${date} (Check In)`);
                columns.push(`${date} (Check Out)`);
            });

            const rows = processedData.newList.map((student, i) => {
                const row = [i + 1, student.StudentName];
                if (hasRegNo) row.push(student.data[0]?.RegNo || "");
                processedData.uniqueDates.forEach(date => {
                    const data = processedData.attendanceData[student.StudentName][date];
                    if (data) {
                        row.push(data.CheckIn || "No Check In");
                        row.push(data.CheckOut || "No Check Out");
                    } else {
                        row.push("No Check In");
                        row.push("No Check Out");
                    }
                });
                return row;
            });

            doc.autoTable({
                head: [columns],
                body: rows,
                startY: 85,
                theme: 'grid',
                styles: { fontSize: 7, cellPadding: 2 },
                headStyles: { fillColor: [41, 128, 185], textColor: 255 },
                alternateRowStyles: { fillColor: [240, 240, 240] }
            });

            doc.save(`Attendance_${className}_${sectionName}_${config.value}.pdf`);
            toast.success("PDF file exported successfully");
            return;
        }

        // Prepare table columns and rows
        let columns = [];
        let rows = [];

        switch (config.reportCategory) {
            case "dateWise":
                columns = ["S.N.", "Student Name", "Reg. No.", "Date", "Day", "Check In", "Check Out", "Status"];
                rows = filteredData.map((item, index) => [
                    index + 1,
                    item.StaffName || item.FullName || item.StudentName || "",
                    item.RegNo || "",
                    item.AttenDate || "",
                    item.AttenDay || "",
                    item.CheckIn || "-",
                    item.CheckOut || "-",
                    item.CheckIn && item.CheckOut ? "Present" : (item.CheckIn ? "Partial" : "Absent")
                ]);
                break;
            case "monthWise":
                columns = ["S.N.", "Student Name", "Reg. No.", "Month", "Present Days", "Absent Days", "Total Days", "Attendance %"];
                rows = filteredData.map((item, index) => [
                    index + 1,
                    item.StaffName || item.FullName || item.StudentName || "",
                    item.RegNo || "",
                    item.Month || "",
                    item.PresentDays || 0,
                    item.AbsentDays || 0,
                    item.TotalDays || (item.PresentDays + item.AbsentDays),
                    item.AttendancePercentage ? `${item.AttendancePercentage}%` : "0%"
                ]);
                break;
            default:
                break;
        }

        doc.autoTable({
            head: [columns],
            body: rows,
            startY: 85,
            theme: 'striped',
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [41, 128, 185], textColor: 255 },
            alternateRowStyles: { fillColor: [240, 240, 240] }
        });

        doc.save(`Attendance_${className}_${sectionName}_${config.value}.pdf`);
        toast.success("PDF file exported successfully");
    };

    // Dynamic columns based on report type
    const getColumns = () => {
        const baseColumns = [
            {
                name: "S.N.",
                selector: (row, index) => index + 1,
                sortable: true,
                width: "80px",
                center: true
            },
            {
                name: "Student Name",
                selector: row => row.StaffName || row.FullName || row.StudentName,
                sortable: true,
                grow: 2
            },
        ];

        switch (config.reportCategory) {
            case "dateWise":
                return [
                    ...baseColumns,
                    { name: "Reg. No.", selector: row => row.RegNo, sortable: true, center: true },
                    { name: "Date", selector: row => row.AttenDate, sortable: true, center: true },
                    { name: "Day", selector: row => row.AttenDay, sortable: true, center: true },
                    { name: "Check In", selector: row => row.CheckIn || "-", sortable: true, center: true },
                    { name: "Check Out", selector: row => row.CheckOut || "-", sortable: true, center: true },
                    // { name: "Check In Via", selector: row => row.CheckInVia || "-", sortable: true, center: true },
                    // { name: "Check Out Via", selector: row => row.CheckOutVia || "-", sortable: true, center: true },
                    // {
                    //     name: "Status",
                    //     selector: row => {
                    //         if (row.CheckIn && row.CheckOut) return "Present";
                    //         if (row.CheckIn) return "Partial";
                    //         return "Absent";
                    //     },
                    //     sortable: true,
                    //     center: true,
                    //     cell: row => {
                    //         const status = row.CheckIn && row.CheckOut ? "Present" : (row.CheckIn ? "Partial" : "Absent");
                    //         const color = status === "Present" ? "#28a745" : (status === "Partial" ? "#ffc107" : "#dc3545");
                    //         return <span className="badge" style={{ backgroundColor: color, color: "white" }}>{status}</span>;
                    //     }
                    // },
                ];
            case "monthWise":
                return [
                    ...baseColumns,
                    { name: "Reg. No.", selector: row => row.RegNo, sortable: true, center: true },
                    { name: "Month", selector: row => row.Month, sortable: true, center: true },
                    { name: "Present Days", selector: row => row.PresentDays || 0, sortable: true, center: true },
                    { name: "Absent Days", selector: row => row.AbsentDays || 0, sortable: true, center: true },
                    { name: "Total Days", selector: row => row.TotalDays || (row.PresentDays + row.AbsentDays), sortable: true, center: true },
                    {
                        name: "Attendance %",
                        selector: row => row.AttendancePercentage ? `${row.AttendancePercentage}%` : "0%",
                        sortable: true,
                        center: true,
                        cell: row => {
                            const percentage = parseFloat(row.AttendancePercentage) || 0;
                            let color = "#dc3545";
                            if (percentage >= 75) color = "#28a745";
                            else if (percentage >= 60) color = "#ffc107";
                            return <span className="badge" style={{ backgroundColor: color, color: "white" }}>{percentage}%</span>;
                        }
                    },
                ];
            default:
                return baseColumns;
        }
    };

    // Data processing for monthwise matrix views
    const getProcessedData = () => {
        const data = searchText ? filteredData : reportData;
        if (!data.length) return { newList: [], uniqueDates: [], attendanceData: {}, viewTwo: [] };

        const getName = (item) => item.StaffName || item.FullName || item.StudentName || "Unknown";

        const groupedByName = data.reduce((acc, curr) => {
            const name = getName(curr);
            if (acc[name]) {
                acc[name].push(curr);
            } else {
                acc[name] = [curr];
            }
            return acc;
        }, {});

        const newList = Object.keys(groupedByName).map((name) => ({
            StudentName: name,
            data: groupedByName[name].reverse(),
        }));

        let uniqueDates = [...new Set(data.map((item) => item.AttenDate))].filter(d => d);
        uniqueDates = selectedSortBy === "A" ? uniqueDates : uniqueDates.reverse();

        const attendanceData = {};
        newList.forEach((item) => {
            attendanceData[item.StudentName] = {};
            uniqueDates.forEach((date) => {
                const record = item.data.find((d) => d.AttenDate === date);
                if (record) {
                    attendanceData[item.StudentName][date] = {
                        CheckIn: record.CheckIn,
                        CheckOut: record.CheckOut,
                    };
                } else {
                    attendanceData[item.StudentName][date] = null;
                }
            });
        });

        const viewTwo = [];
        for (let i = 0; i < uniqueDates.length; i += 6) {
            viewTwo.push(uniqueDates.slice(i, i + 6));
        }

        return { newList, uniqueDates, attendanceData, viewTwo };
    };

    const processedData = useMemo(() => getProcessedData(), [reportData, filteredData, searchText, selectedSortBy]);

    const hasRegNo = useMemo(() => {
        const data = searchText ? filteredData : reportData;
        return data.some(item => item.RegNo);
    }, [reportData, filteredData, searchText]);

    // Custom loader component
    const CustomLoader = () => (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px', flexDirection: 'column' }}>
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Generating report...</p>
        </div>
    );

    const noDataMessage = (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px', flexDirection: 'column' }}>
            <i className="fa fa-qrcode" style={{ fontSize: '48px', color: '#ccc' }}></i>
            <p className="mt-2 text-muted">No data found</p>
        </div>
    );

    return (
        <>
            {/* Controls */}
            <div className="row w-100 m-0 p-0" >
                <div className="col-md-2 col-sm-2 col-lg-2 dropdown">
                    <label className="list-label">Report Category</label>
                    <select
                        style={{ fontSize: "11px" }}
                        name="status"
                        className="form-control form-control-sm"
                        aria-label="Default select example"
                        value={config.reportCategory}
                        onChange={(e) => handleReportCategoryChange(e.target.value)}
                    >
                        <option value="dateWise">Date Wise Report</option>
                        <option value="monthWise">Month Wise Summary</option>
                    </select>
                    <i className="fas fa-angle-down notificationpositon"></i>
                </div>

                <div className="col-md-2 col-sm-2 col-lg-2 dropdown">
                    <label className="list-label">Class</label>
                    <select
                        style={{ fontSize: "11px" }}
                        name="status"
                        className="form-control form-control-sm"
                        aria-label="Default select example"
                        value={config.class}
                        onChange={(e) => handleClassChange(e.target.value)}
                        disabled={!config.reportCategory}
                    >
                        <option value="" style={{ fontSize: "11px" }}>Select Class</option>
                        {classList?.map((item) => (
                            <option key={item.ClassID} value={item.ClassID}>
                                {item.ClassName}
                            </option>
                        ))}
                    </select>
                    <i className="fas fa-angle-down notificationpositon"></i>
                </div>
                <div className="col-md-2 col-sm-2 col-lg-2 dropdown">
                    <label className="list-label">Section</label>
                    <select
                        style={{ fontSize: "11px" }}
                        name="status"
                        className="form-control form-control-sm"
                        aria-label="Default select example"
                        value={config.section}
                        onChange={(e) => handleSectionChange(e.target.value)}
                        disabled={!config.class}
                    >
                        <option value="" style={{ fontSize: "11px" }}>Select Section</option>
                        {sectionList?.map((item) => (
                            <option key={item.SectionID} value={item.SectionID}>
                                {item.SectionName}
                            </option>
                        ))}
                    </select>
                    <i className="fas fa-angle-down notificationpositon"></i>
                </div>

                {config.reportCategory === "dateWise" && (
                    <div className="col-md-2 col-sm-2 col-lg-2 dropdown2">
                        <label className="list-label">Date</label>
                        <Calendar
                            className="form-control form-control-sm"
                            dateFormat="YYYY/MM/DD"
                            language="en"
                            values={config.value}
                            onChange={handleFromDate}
                        />
                    </div>
                )}

                {(config.reportCategory === "monthWise") && (
                    <>

                        <div className="col-md-2 col-sm-2 col-lg-2 dropdown2">
                            <label className="list-label">Select Year</label>
                            <select
                                style={{ fontSize: "11px", marginBottom: "3px" }}
                                name="snotifiaction"
                                value={selectYear}
                                onChange={handleYear}
                                className="form-control form-control-sm"
                                aria-label="Default select example "
                            >
                                <option
                                    value=""
                                    disabled
                                    selected
                                    style={{ fontSize: "11px" }}
                                >
                                    Select Year
                                </option>
                                {date.map((item) => (
                                    <option key={item} value={item}>
                                        {item}
                                    </option>
                                )).reverse()}
                            </select>
                            <i class="fas fa-angle-down  notificationpositon"></i>
                        </div>

                        <div className="col-md-2 col-sm-2 col-lg-2 dropdown">
                            <label className="list-label">Select Month</label>
                            <select
                                style={{ fontSize: "11px" }}
                                name="status"
                                className="form-control form-control-sm"
                                aria-label="Default select example"
                                value={selectMonth}
                                onChange={handleMonthChange}
                            >
                                <option value="" style={{ fontSize: "11px" }}>Select Month</option>
                                <option value="01">Baishakh</option>
                                <option value="02">Jestha</option>
                                <option value="03">Asar</option>
                                <option value="04">Shawan</option>
                                <option value="05">Bhadra</option>
                                <option value="06">Asoj</option>
                                <option value="07">Kartik</option>
                                <option value="08">Mangsir</option>
                                <option value="09">Poush</option>
                                <option value="10">Magh</option>
                                <option value="11">Falgun</option>
                                <option value="12">Chaitra</option>
                            </select>
                            <i className="fas fa-angle-down notificationpositon"></i>
                        </div>

                        <div className="col-md-2 col-sm-2 col-lg-2 dropdown">
                            <label className="list-label">Select View</label>
                            <select
                                style={{ fontSize: "11px" }}
                                name="view"
                                className="form-control form-control-sm"
                                aria-label="Default select example"
                                value={selectView}
                                onChange={handleView}
                            >
                                <option value="1">View 1</option>
                                <option value="2">View 2</option>
                            </select>
                            <i className="fas fa-angle-down notificationpositon"></i>
                        </div>
                    </>
                )}

                <div className="col-12 col-md-5 ms-auto" style={{ marginBottom: "10px" }}>
                    <div className="d-flex align-items-center justify-content-end gap-2 flex-wrap">

                        {config.reportCategory === "monthWise" && (
                            <div className="d-flex align-items-center gap-1">
                                <label htmlFor="sortBy" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>Sort By:</label>
                                <select
                                    id="sortBy"
                                    name="sortBy"
                                    value={selectedSortBy}
                                    onChange={(e) => setSelectedSortBy(e.target.value)}
                                    style={{ fontSize: "11px" }}
                                    className="form-control form-control-sm"
                                >
                                    <option value="A">Ascending Date</option>
                                    <option value="D">Descending Date</option>
                                </select>
                            </div>
                        )}

                        {/* Search */}
                        <input
                            placeholder="Search..."
                            ref={searchInput}
                            type="text"
                            className="form-control form-control-sm"
                            style={{ maxWidth: "180px" }}
                            onChange={searchHandler}
                            disabled={!reportData.length}
                        />

                        {/* Export Excel */}
                        <Tippy content="Export to Excel">
                            <Tippy content="PDF">
                                <button
                                    className="exportButtons"
                                    onClick={handleExportExcel}
                                >
                                    <i className="bi bi-file-earmark-excel-fill exportButtonIconsExcel"></i>
                                </button>
                            </Tippy>
                        </Tippy>

                        {/* Export PDF */}
                        <Tippy content="Export to PDF">
                            <button className="exportButtons" onClick={handleExportPDF}>
                                <i className="bi bi-file-earmark-pdf-fill exportButtonIconsPdf"></i>
                            </button>
                        </Tippy>

                    </div>
                </div>
            </div>

            {isLoading ? (
                <CustomLoader />
            ) : (
                <>
                    {/* View 1: Full matrix - dates as columns with Check In/Check Out sub-columns */}
                    {config.reportCategory === "monthWise" && selectView === "1" && reportData.length > 0 ? (
                        <div className="table-responsive br-3">
                            <table className="table table-striped table-bordereds text-nowrap mb-0" id="table-to-xls">
                                <thead>
                                    <tr className="tableHead">
                                        <td
                                            rowSpan="2"
                                            className="rdt_TableHeadRow tableHead text-center"
                                            width="70px"
                                            style={{ verticalAlign: "middle", borderBottom: "0px", textAlign: "center", fontWeight: "bold" }}
                                        >
                                            S.N.
                                        </td>
                                        <td
                                            rowSpan="2"
                                            className="rdt_TableHeadRow tableHead text-start"
                                            width="200px"
                                            style={{ verticalAlign: "middle", borderBottom: "0px", fontWeight: "bold" }}
                                        >
                                            Full Name
                                        </td>
                                        {hasRegNo && (
                                            <td
                                                rowSpan="2"
                                                className="rdt_TableHeadRow tableHead text-start"
                                                width="100px"
                                                style={{ verticalAlign: "middle", borderBottom: "0px", fontWeight: "bold" }}
                                            >
                                                Reg. No.
                                            </td>
                                        )}
                                        {processedData.uniqueDates.map((date) => (
                                            <td
                                                colSpan="2"
                                                className="rdt_TableHeadRow tableHead text-center"
                                                width="200px"
                                                style={{ fontWeight: "bold", borderBottom: "0px", textAlign: "center" }}
                                                key={date}
                                            >
                                                {date}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr className="head-bottom">
                                        {processedData.uniqueDates.map((date) => (
                                            <React.Fragment key={date}>
                                                <td
                                                    className="rdt_TableHeadRow tableHead text-center"
                                                    width="150px"
                                                    style={{ fontWeight: "bold", borderBottom: "0px", textAlign: "center" }}
                                                >
                                                    Check In
                                                </td>
                                                <td
                                                    className="rdt_TableHeadRow tableHead text-center"
                                                    width="150px"
                                                    style={{ fontWeight: "bold", borderBottom: "0px", textAlign: "center" }}
                                                >
                                                    Check Out
                                                </td>
                                            </React.Fragment>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {processedData.newList.map((student, i) => (
                                        <tr key={i}>
                                            <td style={{ textAlign: "center" }} className="rdt_TableCell tablecell text-center">
                                                {i + 1}
                                            </td>
                                            <td className="rdt_TableCell tablecell text-start">
                                                {student.StudentName}
                                            </td>
                                            {hasRegNo && (
                                                <td className="rdt_TableCell tablecell text-center">
                                                    {student.data[0]?.RegNo || "-"}
                                                </td>
                                            )}
                                            {processedData.uniqueDates.map((date) => {
                                                const data = processedData.attendanceData[student.StudentName][date];
                                                if (data) {
                                                    return (
                                                        <React.Fragment key={date}>
                                                            <td className="rdt_TableCell tablecell text-center" style={{ textAlign: "center" }}>
                                                                {data.CheckIn || "No Check In"}
                                                            </td>
                                                            <td className="rdt_TableCell tablecell text-center" style={{ textAlign: "center" }}>
                                                                {data.CheckOut || "No Check Out"}
                                                            </td>
                                                        </React.Fragment>
                                                    );
                                                } else {
                                                    return (
                                                        <React.Fragment key={date}>
                                                            <td className="rdt_TableCell tablecell text-center" style={{ textAlign: "center" }}>
                                                                No Check In
                                                            </td>
                                                            <td className="rdt_TableCell tablecell text-center" style={{ textAlign: "center" }}>
                                                                No Check Out
                                                            </td>
                                                        </React.Fragment>
                                                    );
                                                }
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    /* View 2: Grouped matrix - dates in chunks of 6 */
                    ) : config.reportCategory === "monthWise" && selectView === "2" && reportData.length > 0 ? (
                        <div className="table-responsive br-3">
                            <table className="table new__table table-striped table-bordereds text-nowrap mb-0" id="table-to-xls">
                                <thead>
                                    <tr className="tableHead">
                                        <td
                                            className="rdt_TableHeadRow tableHead text-center"
                                            width="70px"
                                            style={{ textAlign: "center", verticalAlign: "middle", borderBottom: "0px", fontWeight: "bold" }}
                                        >
                                            S.N.
                                        </td>
                                        <td
                                            className="rdt_TableHeadRow tableHead text-center"
                                            colSpan={processedData.uniqueDates.length < 6 ? processedData.uniqueDates.length : 6}
                                            style={{ borderBottom: "0px", textAlign: "center" }}
                                        >
                                        </td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {processedData.newList.map((student, i) => (
                                        <React.Fragment key={i}>
                                            <tr>
                                                <td
                                                    rowSpan={processedData.viewTwo.length + 1}
                                                    className="rdt_TableCell tablecell text-center"
                                                    style={{ textAlign: "center", fontWeight: "bold" }}
                                                >
                                                    {i + 1}
                                                </td>
                                                <td
                                                    colSpan={processedData.uniqueDates.length < 6 ? processedData.uniqueDates.length : 6}
                                                    style={{ textAlign: "center", backgroundColor: "#eee", fontWeight: "bold" }}
                                                    className="rdt_TableCell tablecell font-weight-bold text-center"
                                                >
                                                    {student.StudentName}
                                                </td>
                                            </tr>
                                            {processedData.viewTwo.map((row, rowIndex) => (
                                                <tr className="new__table__row" key={rowIndex}>
                                                    {row.map((date) => {
                                                        const data = processedData.attendanceData[student.StudentName][date];
                                                        return (
                                                            <td
                                                                className="rdt_TableHeadRow tableHead text-center"
                                                                style={{ borderBottom: "0px", textAlign: "center" }}
                                                                key={date}
                                                            >
                                                                <div className="table__date">{date}</div>
                                                                <div className="table__time">
                                                                    {data ? (
                                                                        <React.Fragment>
                                                                            {data.CheckIn || "No Check In"} - {data.CheckOut || "No Check Out"}
                                                                        </React.Fragment>
                                                                    ) : (
                                                                        "No Check In/Out"
                                                                    )}
                                                                </div>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    /* No data for matrix views */
                    ) : config.reportCategory === "monthWise" && (selectView === "1" || selectView === "2") && reportData.length === 0 ? (
                        noDataMessage
                    /* Default DataTable for dateWise and other cases */
                    ) : (
                        <DataTable
                            columns={getColumns()}
                            data={searchText ? filteredData : reportData}
                            customStyles={customStyles}
                            noDataComponent={noDataMessage}
                            pagination
                            paginationPerPage={10}
                            paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 30, 50, 100]}
                            fixedHeader
                            fixedHeaderScrollHeight="500px"
                            highlightOnHover
                            pointerOnHover
                            responsive
                            dense
                            striped
                        />
                    )}
                </>
            )}
        </>
    )
}

export default DeviceCodeReport;